import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type BetaState = {
  enabled: boolean;
  cap: number;
  remaining: number;
  /** False when the remaining-seats RPC failed; UI should show cap-only copy. */
  remainingKnown: boolean;
  primaryProfileCount: number;
  loading: boolean;
  error: string | null;
};

const initial: BetaState = {
  enabled: false,
  cap: 0,
  remaining: 0,
  remainingKnown: false,
  primaryProfileCount: 0,
  loading: true,
  error: null,
};

// Module-level cache so navigations after the first mount render the strip
// synchronously instead of flashing in once the fetch resolves. This prevents
// the layout-shift stutter on marketing routes (Home → Pricing in particular).
let cached: Omit<BetaState, "loading" | "error"> | null = null;

export function useBetaState() {
  const [state, setState] = useState<BetaState>(() =>
    cached ? { ...cached, loading: false, error: null } : initial,
  );

  const refresh = useCallback(async () => {
    setState((prev) => ({ ...prev, error: null }));
    const [stateRes, remainingRes] = await Promise.all([
      supabase.from("beta_launch_state").select("enabled, cap").eq("id", true).maybeSingle(),
      supabase.rpc("beta_remaining"),
    ]);

    // The state read is the load-bearing call — if it fails we can't render
    // the strip at all. The remaining-seats RPC is best-effort: if it fails
    // (e.g. anon EXECUTE was revoked), we still render the strip with
    // cap-only copy so the layout never collapses post-load.
    if (stateRes.error) {
      setState((prev) => ({ ...prev, loading: false, error: stateRes.error!.message }));
      return;
    }

    const enabled = stateRes.data?.enabled ?? false;
    const cap = stateRes.data?.cap ?? 0;
    const remainingKnown = !remainingRes.error;
    const remaining = remainingKnown ? ((remainingRes.data as number | null) ?? 0) : cap;
    const primaryProfileCount = remainingKnown ? Math.max(0, cap - remaining) : 0;

    cached = { enabled, cap, remaining, remainingKnown, primaryProfileCount };
    setState({
      enabled,
      cap,
      remaining,
      remainingKnown,
      primaryProfileCount,
      loading: false,
      error: remainingRes.error?.message ?? null,
    });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { ...state, refresh };
}
